/**
 * @file RegionSelector.tsx
 *
 * The region dropdown panel rendered inside {@link DropdownSelection}: an
 * "Everywhere" row, then the continent → country → state tree (already filtered by
 * the field's search query), with a Cancel / Save footer. Selecting "Everywhere" is
 * mutually exclusive with any specific region (selecting it clears the rest;
 * selecting a specific region drops the sentinel). Selection is staged in the
 * draft owned by `DropdownSelection`; this reports changes via `setSelected` and
 * commits/aborts via `onSave`/`onCancel`. Mirrors WSB-v2's RegionSelector.
 */

import {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
  type SyntheticEvent,
} from 'react';
import { Button, Divider } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type { ShippingZoneLocation, ShopContinent } from '../../api/shop-types';
import { ALL_REGIONS_LOCATION } from '../shipping-constants';
import {
  getContinentLocation,
  getCountryLocation,
  getLocationKey,
  getStateLocation,
  getStateLocationCode,
  isAllRegionsLocation,
  removeAllRegionsLocation,
} from '../shipping-region-utils';
import { RegionOption } from './RegionOption';
import styles from './RegionSelector.module.css';

interface RegionSelectorProps {
  /** The region tree to render (already filtered by `searchQuery`). */
  data: ShopContinent[];
  selected: ShippingZoneLocation[];
  setSelected: Dispatch<SetStateAction<ShippingZoneLocation[]>>;
  searchQuery: string;
  onSave: (event?: SyntheticEvent) => void;
  onCancel: (event?: SyntheticEvent) => void;
}

/**
 * Renders the region tree panel with its Cancel / Save footer.
 *
 * @param props - The (filtered) tree, draft selection + setter, query, and the
 *   save/cancel handlers.
 *
 * @returns The dropdown panel element.
 */
export function RegionSelector({
  data,
  selected,
  setSelected,
  searchQuery,
  onSave,
  onCancel,
}: RegionSelectorProps) {
  const intl = useIntl();
  const hasQuery = searchQuery.trim().length > 0;
  const selectedKeys = useMemo(() => new Set(selected.map(getLocationKey)), [selected]);
  const expandLabel = intl.formatMessage(adminMessages.shippingRegionExpand);
  const collapseLabel = intl.formatMessage(adminMessages.shippingRegionCollapse);

  const handleToggle = useCallback(
    (location: ShippingZoneLocation, checked: boolean): void => {
      setSelected((prev) => {
        if (isAllRegionsLocation(location)) {
          return [ALL_REGIONS_LOCATION];
        }
        const withoutAll = removeAllRegionsLocation(prev);
        const key = getLocationKey(location);
        if (checked) {
          return withoutAll.some((l) => getLocationKey(l) === key)
            ? withoutAll
            : [...withoutAll, location];
        }
        return withoutAll.filter((l) => getLocationKey(l) !== key);
      });
    },
    [setSelected],
  );

  return (
    <>
      <div className={`gv-overflow-y-scroll gv-p-md ${styles.list}`}>
        {data.length === 0 ? (
          <p className="gv-text-sm">
            <FormattedMessage {...adminMessages.shippingRegionNoResults} />
          </p>
        ) : (
          <>
            <RegionOption
              location={ALL_REGIONS_LOCATION}
              label={intl.formatMessage(adminMessages.shippingEverywhere)}
              selectedKeys={selectedKeys}
              onToggle={handleToggle}
            />
            <Divider className="gv-my-xs" />
            {data.map((continent) => (
              <RegionOption
                key={continent.code}
                location={getContinentLocation(continent.code)}
                label={continent.name}
                selectedKeys={selectedKeys}
                expanded={hasQuery}
                expandLabel={expandLabel}
                collapseLabel={collapseLabel}
                onToggle={handleToggle}
                renderChildren={
                  continent.countries.length > 0
                    ? () => (
                        <>
                          {continent.countries.map((country) => (
                            <div key={country.code} className="gv-pl-lg">
                              <RegionOption
                                location={getCountryLocation(country.code)}
                                label={country.name}
                                selectedKeys={selectedKeys}
                                expanded={hasQuery}
                                expandLabel={expandLabel}
                                collapseLabel={collapseLabel}
                                onToggle={handleToggle}
                                renderChildren={
                                  country.states.length > 0
                                    ? () => (
                                        <>
                                          <Divider className="gv-my-xs" />
                                          {country.states.map((state) => (
                                            <div
                                              key={getStateLocationCode(country.code, state.code)}
                                              className="gv-pl-lg"
                                            >
                                              <RegionOption
                                                location={getStateLocation(country.code, state.code)}
                                                label={state.name}
                                                selectedKeys={selectedKeys}
                                                onToggle={handleToggle}
                                              />
                                              <Divider className="gv-my-xs" />
                                            </div>
                                          ))}
                                        </>
                                      )
                                    : undefined
                                }
                              />
                            </div>
                          ))}
                        </>
                      )
                    : undefined
                }
              />
            ))}
          </>
        )}
      </div>

      <div className="gv-flex gv-gap-sm gv-flex-col-reverse gv-tab-flex-row gv-m-md">
        <Button
          variant="cancel"
          condensed
          data-testid="shipping-zone-form-regions-cancel"
          onClick={onCancel}
        >
          <FormattedMessage {...adminMessages.shippingMethodCancel} />
        </Button>
        <Button
          variant="primary"
          condensed
          data-testid="shipping-zone-form-regions-save"
          onClick={onSave}
        >
          <FormattedMessage {...adminMessages.shippingRegionsSave} />
        </Button>
      </div>
    </>
  );
}
